<template>
    <div id="loan-review" class="m-4">
        <!-- TITLE -->
        <div class="vx-row mb-4">
            <div class="vx-col w-full lg:w-full">
                <vx-card class="mt-2" card-background="dark">
                    <h1 class="bold big-title m-0 text-white">
                        Pre-revisión <span class="success">originación</span>.</h1>
                </vx-card>
            </div>
        </div>
        <!-- TABLE -->
        <div class="vx-row">
            <div class="vx-col w-full lg:w-full">
                <vx-card>
                    <vs-table stripe max-items="25" :data="projects" pagination search noDataText="No hay datos disponibles">
                        <div class="flex align-items-end" slot="header">
                            <h4 class="mr-4">Solicitudes de crédito</h4>
                            <vs-button color="primary" icon-pack="feather"
                                    icon="icon-rotate-cw" @click.stop="getData()"></vs-button>
                        </div>
                        <template slot="thead">
                            <vs-th class="bold" v-for="(col, idx) in columns" v-bind:key="idx" :sort-key="col.sortable ? col.key : null" >{{col.title}}</vs-th>
                        </template>
                        <template slot-scope="{data}">
                            <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                                <vs-td :data="data[indextr].pid">
                                    <router-link :to="`/project/${tr.pid}`" target='_blank'>
                                        {{tr.pid}}
                                    </router-link>
                                </vs-td>
                                <vs-td>
                                    <b>{{ tr.applicant.name }}</b><br>
                                    Tel: {{ tr.applicant.phone_code }} {{ phoneFormat(tr.applicant.phone) }}<br>
                                    Correo: {{ tr.applicant.email }}<br>
                                    <span v-if="tr.supplier" class="mb-2">Proveedor: {{ tr.supplier.sid }} | {{ tr.supplier.tradename }}</span>
                                    <vs-chip :color="tr.applicant.has_identity_verification  ? 'primary' : 'danger'" :class="!tr.applicant.has_identity_verification ? 'pulse-danger' : ''">
                                        <span>Verificación de identidad: {{ getStatusStr(tr.applicant.identity_verification_status) }}</span>
                                    </vs-chip>
                                </vs-td>
                                <vs-td>
                                    {{ tr.origination_responsible || '--' }}
                                </vs-td>
                                <vs-td>
                                    {{ tr.credit_risk_responsible || '--' }}
                                </vs-td>
                                <vs-td>
                                    {{ tr.applicant.person_type }}
                                </vs-td>
                                <vs-td>
                                    {{ tr.loan_type }}
                                </vs-td>
                                <vs-td>
                                    {{ tr.status_named }}
                                </vs-td>
                                <vs-td>
                                    <span class="bold">Revisión de Pre-solicitud:</span> <br> {{ sessionFormat(tr.review_date) }} <br>
                                    <span class="bold">Primera revisión:</span> <br> {{ sessionFormat(tr.loan_review_request_timestamp) }} <br>
                                    <span class="bold">Solicitud completa:</span> <br> {{ sessionFormat(tr.documentation_complete_date) }}
                                </vs-td>
                                <vs-td>
                                    {{ tr.time_since_loan_review_request.toFixed(0) }}
                                </vs-td>
                                <vs-td>
                                    <div class="row">
                                        <div class="col-12 ml-2 mt-1"> 
                                            <vs-button size="small" color="primary" type="filled" icon="announcement" @click="showProjectMessages(tr.pid)">Bitácora</vs-button>
                                        </div>
                                        <div class="col-12 ml-2 mt-1"> 
                                            <vs-button size="small" color="warning" type="filled" icon="assignment" @click="showCreditEvents(tr.pid)">Eventos</vs-button>
                                        </div>
                                        <div class="col-12 ml-2 mt-1"> 
                                            <loan-screened-button :loan-id="tr.id" :is-screened="true" @on-save="getData()"></loan-screened-button>
                                        </div>
                                    </div>  
                                </vs-td>
                            </vs-tr>
                        </template>
                    </vs-table>
                </vx-card>
            </div>
        </div>

        <!-- CREDIT EVENTS -->
        <vs-popup title="Eventos de la solicitud" :active.sync="showFormPromptCreditEvents">
            <CreditEventsLog ref="creditEvents" class="mt-4" :projectId="currentProjectId" />
        </vs-popup>

        <!-- MESSAGES -->
        <vs-popup title="Mensajero" :active.sync="showFormPromptMessages">
            <ProjectMessageLogger ref="projectMessage"></ProjectMessageLogger>
        </vs-popup>
    </div>
</template>

<script>
import formatHelper from '@mixins/formatHelper';
import dateHelper from '@mixins/dateHelper';
import CreditEventsLog from '@components/projectsv2/CreditEventsLog';
import ProjectMessageLogger from '@components/ProjectMessageLogger';
import LoanScreenedButton from '../../../../components/project-details/LoanScreenedButton.vue';
import userIdentityHelper from '../../../../helpers/userIdentityHelper';


const columns = [
    { title: "PID", key: "pid", sortable: true }
    , { title: "SOLICITANTE", key: "client", sortable: true }
    , { title: "EJECUTIVO DE ORIGINACIÓN", key: "origination_responsible", sortable: true }
    , { title: "EJECUTIVO DE RIESGO", key: "credit_risk_responsible", sortable: true }
    , { title: "TIPO", key: "type", sortable: true }
    , { title: "CRÉDITO", key: "credit", sortable: true }
    , { title: "ESTATUS", key: "status", sortable: true }
    , { title: "FECHA", key: "date", sortable: true }
    , { title: "TT (días)", key: "turnaroundTime", sortable: true }
    , { title: "ACCIONES", key: "actions", sortable: false }
];

export default {
    data: () => ({
        columns: columns,
        projects: [],
        showFormPromptCreditEvents: false,
        showFormPromptMessages: false,
        currentProjectId: null
    }),
    mixins: [
        formatHelper,
        dateHelper,
        userIdentityHelper
    ],
    components: {
        CreditEventsLog,
        ProjectMessageLogger,
        LoanScreenedButton
    },
    mounted() {
        this.getData();
    },
    methods: {
        async getData() {
            try {
                this.showLoading(true, "Cargando solicitudes de crédito...");
                const response = await axios.get("/api/v2/projects/getPreRequestOriginationList");
                this.projects = response.data;
                this.showLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                this.showLoading(false);
            } finally {
                this.showLoading(false);
            }
        },
        showCreditEvents(pid){
            this.currentProjectId = pid;
            this.showFormPromptCreditEvents = true;
            this.$refs.creditEvents.getEvents(pid);
        },
        showProjectMessages(pid){
            this.showFormPromptMessages = true;
            this.$refs.projectMessage.setProjectId(pid);
            this.$refs.projectMessage.getNotes(pid);
        },
    }
}
</script>